export type MealPlanRestrictionSource = {
  allergens?: unknown;
  allergenTagIds?: unknown;
  intoleranceTags?: unknown;
  intoleranceTagIds?: unknown;
} | null | undefined;

export type ClientRestrictionProfile = {
  allergies?: unknown;
  foodIntolerances?: unknown;
} | null | undefined;

export type MealPlanRestrictionWarning = {
  allergyMatches: string[];
  intoleranceMatches: string[];
  message: string;
};

export type MealPlanRestrictionLabels = {
  allergenLabels: string[];
  intoleranceLabels: string[];
};

const ALLERGEN_ALIASES: Record<string, string> = {
  almond: 'tree_nut',
  almonds: 'tree_nut',
  barley: 'gluten',
  brazilnut: 'tree_nut',
  brazilnuts: 'tree_nut',
  cashew: 'tree_nut',
  cashews: 'tree_nut',
  casein: 'dairy',
  crab: 'shellfish',
  dairy: 'dairy',
  egg: 'egg',
  eggs: 'egg',
  fish: 'fish',
  gluten: 'gluten',
  hazelnut: 'tree_nut',
  hazelnuts: 'tree_nut',
  lactose: 'lactose',
  lobster: 'shellfish',
  macadamia: 'tree_nut',
  macadamias: 'tree_nut',
  maize: 'corn',
  milk: 'dairy',
  peanut: 'peanut',
  peanuts: 'peanut',
  pecan: 'tree_nut',
  pecans: 'tree_nut',
  pistachio: 'tree_nut',
  pistachios: 'tree_nut',
  prawn: 'shellfish',
  prawns: 'shellfish',
  rye: 'gluten',
  sesame: 'sesame',
  shellfish: 'shellfish',
  shrimp: 'shellfish',
  soya: 'soy',
  soy: 'soy',
  sulphite: 'sulfite',
  sulphites: 'sulfite',
  sulfite: 'sulfite',
  sulfites: 'sulfite',
  treenut: 'tree_nut',
  treenuts: 'tree_nut',
  walnut: 'tree_nut',
  walnuts: 'tree_nut',
  wheat: 'gluten',
  whey: 'dairy',
};

const INTOLERANCE_ALIASES: Record<string, string> = {
  barley: 'gluten',
  casein: 'casein',
  dairy: 'dairy',
  fructose: 'fructose',
  gluten: 'gluten',
  lactose: 'lactose',
  maize: 'corn',
  milk: 'dairy',
  rye: 'gluten',
  sulphite: 'sulfite',
  sulphites: 'sulfite',
  sulfite: 'sulfite',
  sulfites: 'sulfite',
  wheat: 'gluten',
  whey: 'dairy',
};

const TITLE_WORDS: Record<string, string> = {
  dairy: 'Dairy',
  egg: 'Egg',
  fish: 'Fish',
  gluten: 'Gluten',
  casein: 'Casein',
  lactose: 'Lactose',
  peanut: 'Peanut',
  shellfish: 'Shellfish',
  soy: 'Soy',
  sulfite: 'Sulfite',
  sesame: 'Sesame',
  tree_nut: 'Tree nut',
};

function tokenFromUnknown(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (!value || typeof value !== 'object' || Array.isArray(value)) return '';

  const record = value as Record<string, unknown>;
  for (const key of ['label', 'name', 'value', 'id', 'slug', 'key']) {
    const token = tokenFromUnknown(record[key]);
    if (token.trim()) return token;
  }
  return '';
}

function readStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.flatMap(readStringArray);
  }

  const token = tokenFromUnknown(value).trim();
  if (!token) return [];
  return token
    .split(/[;,]/)
    .map((part) => part.trim())
    .filter(Boolean);
}

function normalizeToken(value: unknown): string {
  return tokenFromUnknown(value)
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/\([^)]*\)/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

function compactToken(value: unknown): string {
  return normalizeToken(value).replace(/\s+/g, '');
}

function canonicalizeAllergen(value: unknown): string {
  const normalized = normalizeToken(value);
  if (!normalized) return '';
  const compact = compactToken(value);
  return ALLERGEN_ALIASES[normalized] || ALLERGEN_ALIASES[compact] || compact;
}

function canonicalizeIntolerance(value: unknown): string {
  const normalized = normalizeToken(value);
  if (!normalized) return '';
  const compact = compactToken(value);
  return INTOLERANCE_ALIASES[normalized] || INTOLERANCE_ALIASES[compact] || compact;
}

function labelForToken(value: string): string {
  const canonical = value.trim();
  if (!canonical) return '';
  const known = TITLE_WORDS[canonical];
  if (known) return known;

  return canonical
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function collectTags(values: unknown, canonicalize: (value: unknown) => string): Map<string, string> {
  const tags = new Map<string, string>();
  for (const raw of readStringArray(values)) {
    const key = canonicalize(raw);
    if (!key || tags.has(key)) continue;
    tags.set(key, labelForToken(key));
  }
  return tags;
}

function addValues(target: Set<string>, values: unknown, canonicalize: (value: unknown) => string): void {
  for (const raw of readStringArray(values)) {
    const key = canonicalize(raw);
    if (key) target.add(key);
  }
}

function formatList(values: string[]): string {
  if (values.length <= 1) return values[0] || '';
  if (values.length === 2) return `${values[0]} and ${values[1]}`;
  return `${values.slice(0, -1).join(', ')}, and ${values[values.length - 1]}`;
}

function buildSourceAllergenMap(source: MealPlanRestrictionSource): Map<string, string> {
  const tags = collectTags(source?.allergens, canonicalizeAllergen);
  for (const [key, label] of collectTags(source?.allergenTagIds, canonicalizeAllergen)) {
    if (!tags.has(key)) tags.set(key, label);
  }

  const sourceIntolerances = buildDirectSourceIntoleranceMap(source);
  for (const dairyDerivedKey of ['lactose', 'casein']) {
    if (sourceIntolerances.has(dairyDerivedKey) && !tags.has(dairyDerivedKey)) {
      tags.set(dairyDerivedKey, labelForToken(dairyDerivedKey));
    }
  }

  return tags;
}

function buildSourceIntoleranceMap(source: MealPlanRestrictionSource, sourceAllergens: Map<string, string>): Map<string, string> {
  const tags = buildDirectSourceIntoleranceMap(source);

  for (const key of sourceAllergens.keys()) {
    if (key === 'dairy' || key === 'gluten' || key === 'sulfite' || key === 'corn') {
      tags.set(key, labelForToken(key));
    }
    if (key === 'dairy') {
      tags.set('lactose', labelForToken('lactose'));
      tags.set('casein', labelForToken('casein'));
    }
  }

  return tags;
}

function buildDirectSourceIntoleranceMap(source: MealPlanRestrictionSource): Map<string, string> {
  const tags = collectTags(source?.intoleranceTags, canonicalizeIntolerance);
  for (const [key, label] of collectTags(source?.intoleranceTagIds, canonicalizeIntolerance)) {
    if (!tags.has(key)) tags.set(key, label);
  }
  return tags;
}

function mapLabels(tags: Map<string, string>): string[] {
  return Array.from(new Set([...tags.values()].filter(Boolean)));
}

function buildClientAllergySet(client: ClientRestrictionProfile): Set<string> {
  const keys = new Set<string>();
  addValues(keys, client?.allergies, canonicalizeAllergen);
  if (keys.has('lactose') || keys.has('casein')) keys.add('dairy');
  return keys;
}

function buildClientIntoleranceSet(client: ClientRestrictionProfile): Set<string> {
  const keys = new Set<string>();
  addValues(keys, client?.foodIntolerances, canonicalizeIntolerance);
  return keys;
}

function matchLabels(sourceTags: Map<string, string>, clientTags: Set<string>): string[] {
  const labels: string[] = [];
  for (const [key, label] of sourceTags) {
    if (clientTags.has(key)) labels.push(label);
  }
  return Array.from(new Set(labels));
}

export function getMealPlanRestrictionLabels(source: MealPlanRestrictionSource): MealPlanRestrictionLabels {
  return {
    allergenLabels: mapLabels(buildSourceAllergenMap(source)),
    intoleranceLabels: mapLabels(buildDirectSourceIntoleranceMap(source)),
  };
}

export function buildMealPlanRestrictionWarning(
  source: MealPlanRestrictionSource,
  client: ClientRestrictionProfile,
  entityLabel = 'template',
): MealPlanRestrictionWarning | null {
  const sourceAllergens = buildSourceAllergenMap(source);
  const sourceIntolerances = buildSourceIntoleranceMap(source, sourceAllergens);
  const allergyMatches = matchLabels(sourceAllergens, buildClientAllergySet(client));
  const intoleranceMatches = matchLabels(sourceIntolerances, buildClientIntoleranceSet(client));

  if (allergyMatches.length === 0 && intoleranceMatches.length === 0) return null;

  const details: string[] = [];
  if (allergyMatches.length > 0) {
    details.push(`allergen${allergyMatches.length === 1 ? '' : 's'}: ${formatList(allergyMatches)}`);
  }
  if (intoleranceMatches.length > 0) {
    details.push(`intolerance${intoleranceMatches.length === 1 ? '' : 's'}: ${formatList(intoleranceMatches)}`);
  }

  return {
    allergyMatches,
    intoleranceMatches,
    message: `This ${entityLabel} includes ${details.join('; ')} that overlap with the client's restrictions. Please review before using it.`,
  };
}
